import { useState } from 'react';
import styled from 'styled-components';

import { Select, SelectType } from 'components/shared/form/InfiniteScrollDropdown';
import InputGroup from 'components/shared/form/InputGroup';
import { colors } from 'styles/theme/colors';
import XIcon from 'svg/XIcon';

type ChippedMultiSelectProps<T> = {
  getOptionValue: (option: T) => string;
  getOptionLabel: (option: T) => string;
  onChange: (options: T[]) => void;
  value: T[];
  inputLabel: string;
  containerLabel: string;
  placeholder: string;
} & Omit<SelectType<T>, 'value' | 'onChange'>;

const ChippedMultiSelect = <T,>(props: ChippedMultiSelectProps<T>) => {
  const { onChange, inputLabel, containerLabel, placeholder, getOptionValue, getOptionLabel } = props;

  const [selectedOptions, setSelectedOptions] = useState<T[]>([]);

  const handleAdd = (option: T) => {
    setSelectedOptions((prev) => [...prev, option]);
    onChange([...selectedOptions, option]);
  };

  const handleDelete = (option: T) => {
    setSelectedOptions((prev) => prev.filter((o) => o !== option));
    onChange(selectedOptions.filter((o) => o !== option));
  };

  return (
    <SelectContainer>
      <InputGroup title={inputLabel}>
        <StyledSelect
          {...props}
          onChange={handleAdd}
          value={null}
          placeholder={placeholder}
          filterOption={(option) => {
            return !selectedOptions.some((o) => getOptionValue(o) === option.value);
          }}
        />
      </InputGroup>
      {selectedOptions.length > 0 && (
        <>
          <ContainerLabel>{containerLabel}:</ContainerLabel>
          <ChipContainer>
            {selectedOptions.map((option) => (
              <Chip key={getOptionValue(option)}>
                {getOptionLabel(option)}
                <StyledXIcon color={colors.black} height={8} width={8} onClick={() => handleDelete(option)} />
              </Chip>
            ))}
          </ChipContainer>
        </>
      )}
    </SelectContainer>
  );
};

const SelectContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
`;

const StyledSelect = styled(Select)`
  width: 100%;
`;

const StyledXIcon = styled(XIcon)`
  cursor: pointer;
`;

const ChipContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
  margin-bottom: 32px;
`;

const ContainerLabel = styled.div`
  font-size: 12px;
  font-weight: bold;
  color: var(--black);
  line-height: 16px;
  margin-bottom: 12px;
`;

export const Chip = styled.div`
  background-color: var(--black-10);
  display: flex;
  align-items: center;
  gap: 8px;
  border-radius: 3;
  padding: 4px 12px;

  font-size: 12px;
  color: var(--black);
  line-height: 16px;
  div {
    cursor: pointer;
  }
`;

export default ChippedMultiSelect;
